import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Group,
  Modal,
  Pill,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconSearch, IconUsersGroup } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import type { Practitioner } from '@medplum/fhirtypes';
import {
  fetchAssignablePractitioners,
  fetchPractitionersWithService,
  toggleAppointmentTypeAccess,
} from '@/lib/healthcareServiceEntitlement';

interface ManageAppointmentTypeAccessModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  /** Name of the appointment type whose access is being managed (for the modal title). */
  serviceName?: string;
}

function practitionerName(practitioner: Practitioner): string {
  return formatPractitionerStaffLabel(practitioner) || practitioner.id || 'Unknown user';
}

function practitionerEmail(practitioner: Practitioner): string | undefined {
  return practitioner.telecom?.find((t) => t.system === 'email')?.value;
}

function practitionerInitials(practitioner: Practitioner): string {
  const name = practitioner.name?.[0];
  const given = name?.given?.[0]?.[0] ?? '';
  const family = name?.family?.[0] ?? '';
  const initials = `${given}${family}`.toUpperCase();
  return initials || practitionerName(practitioner).slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ['gold', 'blue', 'green', 'grape', 'teal', 'orange'] as const;

function avatarColor(id: string): (typeof AVATAR_COLORS)[number] {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
}

/**
 * Grant/revoke staff access to a catalog appointment type (session, meeting, or
 * time-entry) via PractitionerRole.healthcareService.
 */
export function AppointmentTypeAccessPanel({
  serviceId,
}: {
  serviceId: string;
}): JSX.Element | null {
  const medplum = useMedplum();
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!serviceId) return undefined;
    let cancelled = false;
    setSearch('');

    medplum.invalidateSearches('Practitioner');
    medplum.invalidateSearches('PractitionerRole');

    Promise.all([
      fetchAssignablePractitioners(medplum),
      fetchPractitionersWithService(medplum, serviceId),
    ])
      .then(([staff, grantedIds]) => {
        if (cancelled) return;
        const staffIds = new Set(staff.map((p) => p.id as string));
        setPractitioners(staff);
        setSelectedIds(grantedIds.filter((id) => staffIds.has(id)));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[appointment-type-access] failed to load users or access', err);
          setPractitioners([]);
          setSelectedIds([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, serviceId]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const byId = useMemo(
    () => new Map(practitioners.map((p) => [p.id as string, p])),
    [practitioners],
  );

  const selectedPractitioners = useMemo(
    () => selectedIds.map((id) => byId.get(id)).filter((p): p is Practitioner => Boolean(p)),
    [selectedIds, byId],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return practitioners;
    return practitioners.filter((p) => {
      const haystack = `${practitionerName(p)} ${practitionerEmail(p) ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [practitioners, search]);

  const toggle = useCallback(
    async (id: string): Promise<void> => {
      if (!serviceId || pendingIds.has(id)) return;

      const adding = !selectedSet.has(id);
      setPendingIds((prev) => new Set(prev).add(id));
      setSelectedIds((ids) => (adding ? [...ids, id] : ids.filter((x) => x !== id)));

      try {
        await toggleAppointmentTypeAccess(medplum, id, serviceId);
      } catch (err) {
        setSelectedIds((ids) =>
          adding
            ? ids.filter((x) => x !== id)
            : ids.includes(id)
              ? ids
              : [...ids, id],
        );
        notifications.show({
          title: 'Could not update access',
          message: err instanceof Error ? err.message : 'Failed to update user access.',
          color: 'red',
        });
      } finally {
        setPendingIds((prev) => {
          const copy = new Set(prev);
          copy.delete(id);
          return copy;
        });
      }
    },
    [medplum, pendingIds, selectedSet, serviceId],
  );

  const anyPending = pendingIds.size > 0;

  if (!serviceId) return null;

  return (
      <Stack gap="md">
        <TextInput
          placeholder="Search users by name or email"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <div>
          <Text size="sm" fw={600} mb="xs">
            With access ({selectedPractitioners.length})
          </Text>
          {selectedPractitioners.length === 0 ? (
            <Text size="sm" c="dimmed">
              No users have access yet. Add them from the list below.
            </Text>
          ) : (
            <Group gap="xs">
              {selectedPractitioners.map((p) => (
                <Pill
                  key={p.id}
                  withRemoveButton
                  onRemove={() => void toggle(p.id as string)}
                  disabled={anyPending}
                  size="md"
                >
                  {practitionerName(p)}
                </Pill>
              ))}
            </Group>
          )}
        </div>

        <Divider />

        <ScrollArea.Autosize mah={360} type="auto">
          <Stack gap={4}>
            {filtered.length === 0 ? (
              <Text size="sm" c="dimmed" py="sm">
                No users match your search.
              </Text>
            ) : (
              filtered.map((p) => {
                const practitionerId = p.id as string;
                const isSelected = selectedSet.has(practitionerId);
                const email = practitionerEmail(p);
                return (
                  <UnstyledButton
                    key={p.id}
                    onClick={() => void toggle(practitionerId)}
                    disabled={anyPending}
                    p="xs"
                    style={{
                      borderRadius: 8,
                      backgroundColor: isSelected ? 'var(--mantine-color-gold-0)' : undefined,
                      opacity: anyPending ? 0.6 : 1,
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" wrap="nowrap">
                        <Avatar color={avatarColor(practitionerId)} radius="xl" size="md">
                          {practitionerInitials(p)}
                        </Avatar>
                        <Box>
                          <Text size="sm" fw={500}>
                            {practitionerName(p)}
                          </Text>
                          {email && (
                            <Text size="xs" c="gray.6">
                              {email}
                            </Text>
                          )}
                        </Box>
                      </Group>
                      {isSelected ? (
                        <Badge color="gold" variant="light" leftSection={<IconCheck size={12} />}>
                          Added
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="outline">
                          Add
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                );
              })
            )}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
  );
}

/**
 * Modal wrapper around {@link AppointmentTypeAccessPanel} for standalone use.
 */
export function ManageAppointmentTypeAccessModal({
  opened,
  onClose,
  serviceId,
  serviceName,
}: ManageAppointmentTypeAccessModalProps): JSX.Element {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconUsersGroup size={18} />
          <Text fw={600}>Manage access{serviceName ? ` — ${serviceName}` : ''}</Text>
        </Group>
      }
      centered
      size="lg"
    >
      <AppointmentTypeAccessPanel serviceId={opened ? serviceId : ''} />
    </Modal>
  );
}
